import React from "react";
import { fmtINR, resolveAssetUrl } from "@/lib/api";
import { amountInWords } from "@/lib/amountInWords";
import { getTheme } from "@/lib/invoiceThemes";
import { patternBg } from "@/lib/patterns";

// Ornate corner flourish SVG (gold)
const Corner = ({ rotate = 0, className = "" }) => (
  <svg viewBox="0 0 80 80" className={className} style={{ transform: `rotate(${rotate}deg)` }} xmlns="http://www.w3.org/2000/svg" fill="none">
    <g stroke="#C9A95D" strokeWidth="0.9" fill="none">
      <path d="M5 5 L 45 5" />
      <path d="M5 5 L 5 45" />
      <path d="M10 10 Q 25 12, 30 25 Q 32 35, 42 38" />
      <path d="M10 10 Q 12 25, 25 30 Q 35 32, 38 42" />
      <circle cx="30" cy="25" r="1.4" fill="#C9A95D" />
      <circle cx="25" cy="30" r="1.4" fill="#C9A95D" />
      <path d="M14 4 Q 18 8, 22 4" />
      <path d="M4 14 Q 8 18, 4 22" />
    </g>
  </svg>
);

const PhoneIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#C9A95D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.37 1.9.72 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.35 1.85.59 2.81.72A2 2 0 0 1 22 16.92z" />
  </svg>
);

const PinIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#C9A95D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

export default function InvoiceDocument({ invoice, profile, client, totals, docType = "invoice" }) {
  const isQuotation = docType === "quotation";
  if (!invoice || !profile || !client) return null;
  const isIGST = invoice.gst_type === "IGST";
  const halfRate = (invoice.gst_rate || 0) / 2;
  const halfGst = (totals.gst_amount || 0) / 2;
  const theme = getTheme(invoice.theme_id);
  const NAVY = theme.text;
  const CREAM = theme.band;
  const CREAM_LIGHT = theme.band;
  const GOLD = theme.accent;
  const BORDER = theme.border;
  const HEADER_BG = theme.header || CREAM;
  const HEADER_FG = theme.headerText || NAVY;
  const PAPER = theme.paperBg || "#FFFFFF";
  const patternStyle = theme.pattern ? patternBg(theme.pattern, theme.accent, PAPER) : { backgroundColor: PAPER };
  const discountAmount = totals.discount_amount || 0;
  const taxable = totals.taxable ?? Math.max(0, (totals.subtotal || 0) - discountAmount);

  return (
    <div
      id="invoice-document-root"
      className="print-area font-mont"
      style={{ width: "210mm", minHeight: "297mm", margin: "0 auto", padding: "12mm 10mm", color: NAVY, fontFamily: "'Montserrat', sans-serif", ...patternStyle }}
    >
      {/* Bordered outer frame with ornate corners */}
      <div className="relative border" style={{ borderColor: BORDER, padding: "18mm 12mm" }}>
        <Corner rotate={0} className="absolute top-1 left-1 w-12 h-12" />
        <Corner rotate={90} className="absolute top-1 right-1 w-12 h-12" />
        <Corner rotate={270} className="absolute bottom-1 left-1 w-12 h-12" />
        <Corner rotate={180} className="absolute bottom-1 right-1 w-12 h-12" />

        {/* HEADER */}
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-5">
            {profile.logo_url ? (
              <img src={resolveAssetUrl(profile.logo_url)} alt="" className="w-24 h-24 object-contain" />
            ) : (
              <div className="w-24 h-24 border flex items-center justify-center text-4xl font-bold" style={{ borderColor: GOLD, color: NAVY }}>
                {(profile.name || "B").charAt(0)}
              </div>
            )}
            <div>
              <div className="text-2xl font-bold leading-tight" style={{ color: NAVY, maxWidth: "320px" }}>{profile.name}</div>
              <div className="mt-3 text-[10px]">
                {profile.gst_number && (
                  <div className="mb-1">
                    <span className="font-bold">GSTIN</span> <span className="ml-1">{profile.gst_number}</span>
                  </div>
                )}
                {profile.phone && (
                  <div className="flex items-center gap-2 mb-1"><PhoneIcon /> {profile.phone}</div>
                )}
                {profile.email && (
                  <div className="mb-1">{profile.email}</div>
                )}
                {profile.address && (
                  <div className="flex items-start gap-2"><span className="mt-0.5"><PinIcon /></span><span className="whitespace-pre-line">{profile.address}</span></div>
                )}
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-xl font-bold tracking-wide" style={{ color: NAVY }}>{isQuotation ? "QUOTATION" : "TAX INVOICE"}</div>
            <div className="mt-2 inline-block border px-3 py-1 text-[9px] tracking-wider uppercase" style={{ borderColor: "#999", color: "#555" }}>Original For Recipient</div>
          </div>
        </div>

        {/* INVOICE META BAND */}
        <div className="mt-6 grid grid-cols-3 px-4 py-3 text-[11px]" style={{ background: CREAM_LIGHT, border: `1px solid ${BORDER}` }}>
          <div>
            <div className="font-bold" style={{ color: NAVY }}>{isQuotation ? "Quotation No." : "Invoice No."}</div>
            <div className="mt-1">{isQuotation ? invoice.quotation_number : invoice.invoice_number}</div>
          </div>
          <div>
            <div className="font-bold" style={{ color: NAVY }}>{isQuotation ? "Quotation Date" : "Invoice Date"}</div>
            <div className="mt-1">{formatDateDMY(invoice.issue_date)}</div>
          </div>
          <div>
            <div className="font-bold" style={{ color: NAVY }}>{isQuotation ? "Valid Until" : "Due Date"}</div>
            <div className="mt-1">{(isQuotation ? invoice.valid_until : invoice.due_date) ? formatDateDMY(isQuotation ? invoice.valid_until : invoice.due_date) : "—"}</div>
          </div>
        </div>

        {/* BILL TO */}
        <div className="mt-5 text-[11px]">
          <div className="font-bold mb-1" style={{ color: NAVY }}>Bill To</div>
          <div className="font-bold text-[13px] uppercase">{client.name}</div>
          {client.address && <div className="text-neutral-700 mt-0.5 whitespace-pre-line">{client.address}</div>}
          {client.gst_number && (
            <div className="mt-1"><span className="font-bold">GSTIN</span> <span className="ml-1">{client.gst_number}</span></div>
          )}
          {client.state && (
            <div className="mt-0.5"><span className="font-bold">Place of Supply</span> <span className="ml-1">{client.state}</span></div>
          )}
        </div>

        {/* LINE ITEMS TABLE */}
        <table className="w-full border-collapse mt-6 text-[11px]">
          <thead>
            <tr style={{ background: HEADER_BG, color: HEADER_FG }}>
              <th className="px-3 py-2 text-left font-semibold w-[6%]">No</th>
              <th className="px-3 py-2 text-left font-semibold">SERVICES</th>
              <th className="px-3 py-2 text-right font-semibold w-[11%]">Qty.</th>
              <th className="px-3 py-2 text-right font-semibold w-[14%]">Rate</th>
              <th className="px-3 py-2 text-right font-semibold w-[16%]">Tax</th>
              <th className="px-3 py-2 text-right font-semibold w-[15%]">Total</th>
            </tr>
          </thead>
          <tbody>
            {(invoice.items || []).map((it, i) => {
              const lineSubtotal = Number(it.quantity || 0) * Number(it.rate || 0);
              const lineTax = lineSubtotal * (invoice.gst_rate / 100);
              const lineTotal = lineSubtotal + lineTax;
              return (
                <tr key={it.id}>
                  <td className="px-3 py-3 align-top">{i + 1}</td>
                  <td className="px-3 py-3 align-top">{it.description}</td>
                  <td className="px-3 py-3 align-top text-right">{it.quantity}</td>
                  <td className="px-3 py-3 align-top text-right">{Number(it.rate).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td className="px-3 py-3 align-top text-right">
                    {lineTax.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    <div className="text-[9px] text-neutral-500">({invoice.gst_rate}%)</div>
                  </td>
                  <td className="px-3 py-3 align-top text-right font-semibold">{Number(lineTotal).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</td>
                </tr>
              );
            })}
            {/* Filler rows for visual breathing room */}
            {Array.from({ length: Math.max(0, 4 - (invoice.items?.length || 0)) }).map((_, i) => (
              <tr key={`f${i}`}><td colSpan={6} className="py-4">&nbsp;</td></tr>
            ))}
          </tbody>
        </table>

        {/* SUBTOTAL BAND */}
        <div className="grid grid-cols-12 items-center px-3 py-3 mt-1 text-[11px] font-semibold" style={{ background: CREAM_LIGHT, color: NAVY }}>
          <div className="col-span-3 uppercase tracking-wide">Subtotal</div>
          <div className="col-span-2 text-right">{(invoice.items || []).reduce((s, it) => s + Number(it.quantity || 0), 0)}</div>
          <div className="col-span-1"></div>
          <div className="col-span-3 text-right">{fmtINR(totals.gst_amount)}</div>
          <div className="col-span-3 text-right">{fmtINR(totals.grand_total)}</div>
        </div>

        {/* RIGHT-ALIGNED TOTALS BLOCK */}
        <div className="mt-5 grid grid-cols-2 gap-8">
          <div></div>
          <div className="text-[11px]">
            <div className="flex justify-between py-1">
              <span>Subtotal</span>
              <span>{fmtINR(totals.subtotal)}</span>
            </div>
            {discountAmount > 0 && (
              <div className="flex justify-between py-1">
                <span>Discount{invoice.discount_type === "percent" ? ` (${invoice.discount}%)` : ""}</span>
                <span>− {fmtINR(discountAmount)}</span>
              </div>
            )}
            <div className="flex justify-between py-1">
              <span>Taxable Amount</span>
              <span>{fmtINR(taxable)}</span>
            </div>
            {isIGST ? (
              <div className="flex justify-between py-1">
                <span>IGST @{invoice.gst_rate}%</span>
                <span>{fmtINR(totals.gst_amount)}</span>
              </div>
            ) : (
              <>
                <div className="flex justify-between py-1">
                  <span>CGST @{halfRate}%</span>
                  <span>{fmtINR(halfGst)}</span>
                </div>
                <div className="flex justify-between py-1">
                  <span>SGST @{halfRate}%</span>
                  <span>{fmtINR(halfGst)}</span>
                </div>
              </>
            )}
            <div className="flex justify-between py-2 border-t border-b font-bold text-[13px] mt-1" style={{ borderColor: NAVY, color: NAVY }}>
              <span>Total Amount</span>
              <span>{fmtINR(totals.grand_total)}</span>
            </div>
            {!isQuotation && (
              <div className="flex justify-between py-1 mt-1">
                <span>Received Amount</span>
                <span>{fmtINR(totals.paid)}</span>
              </div>
            )}
            {!isQuotation && totals.balance_due > 0 && (
              <div className="flex justify-between py-1 font-semibold" style={{ color: NAVY }}>
                <span>Balance Due</span>
                <span>{fmtINR(totals.balance_due)}</span>
              </div>
            )}
          </div>
        </div>

        {/* AMOUNT IN WORDS */}
        <div className="mt-6 text-[11px]">
          <div className="font-bold mb-1" style={{ color: NAVY }}>Total Amount (in words)</div>
          <div className="text-[11px]" style={{ color: NAVY, opacity: 0.85 }}>{amountInWords(totals.grand_total)}</div>
        </div>

        {/* TERMS & CONDITIONS */}
        {invoice.terms && (
          <div className="mt-5 text-[11px]">
            <div className="font-bold mb-1" style={{ color: NAVY }}>Terms & Conditions</div>
            <div className="whitespace-pre-line leading-relaxed" style={{ color: NAVY, opacity: 0.85 }}>{invoice.terms}</div>
          </div>
        )}

        {/* BANK + SIGNATURE */}
        <div className="mt-6 grid grid-cols-12 gap-6">
          <div className="col-span-7 text-[11px]">
            {profile.bank_details && (
              <>
                <div className="font-bold mb-1" style={{ color: NAVY }}>Bank Details</div>
                <div className="whitespace-pre-line leading-relaxed" style={{ color: NAVY, opacity: 0.85 }}>{profile.bank_details}</div>
              </>
            )}
          </div>
          <div className="col-span-5">
            <div className="border h-32 flex flex-col items-center justify-end px-4 pb-2" style={{ borderColor: BORDER }}>
              {profile.signature_url ? (
                <img src={resolveAssetUrl(profile.signature_url)} alt="Signature" className="max-h-20 max-w-full object-contain mb-1" crossOrigin="anonymous" />
              ) : <div className="flex-1" />}
              <div className="border-t w-full text-center pt-1" style={{ borderColor: BORDER }}>
                <div className="text-[11px] font-bold" style={{ color: NAVY }}>{profile.name}</div>
                <div className="text-[9px] tracking-[0.2em] uppercase" style={{ color: NAVY, opacity: 0.6 }}>Authorized Signatory</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function formatDateDMY(iso) {
  if (!iso) return "";
  const m = iso.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return iso;
  return `${m[3]}/${m[2]}/${m[1]}`;
}
